/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool.ts
 * 15-09-2025 - Jelmer Jellema
 * Component Filtertoolscherm (standalone)
 * Selector: filtertool
 */
import {Component, inject, Input, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {Filtertoolregel, FiltertoolRegelTypeNamen, FiltertoolRegelTypes} from '../../services/filtertool/filtertool-regels';
import {maakFiltertoolregel} from '../../services/filtertool/filtertool-regel-factory';
import {Filterboom, filterboomNaarObject, filterboomVanObject, isFilterboomGeldig} from '../../services/filtertool/filtertool-structuur';
import {FiltergroepComponent} from './groep/filtergroep.component';
import {Appscherm} from '../abstracts/appscherm';
import {FiltertoolFactoryService} from '../../services/filtertool/filtertool-factory.service';
import {FiltertoolInstantie} from '../../services/filtertool/filtertool-instantie';
import {GebruikerFilter} from '../../models/gebruiker_filter';
import {Gebruiker} from '../../models/gebruiker';

@Component({
    standalone: true,
    selector: 'filtertool',
    templateUrl: './filtertool.html',
    styleUrls: ['filtertool.scss'],
    imports: [
        // recursieve groepscomponent
        FiltergroepComponent,
        // forms voor ngModel
        FormsModule,
        // ng-select voor nette dropdowns buiten overflow containers
        NgSelectModule,
    ]
})
export class Filtertoolscherm extends Appscherm implements OnInit {
    // Inputs (properties)
    /** Identificatie van de gekoppelde filtertool-instantie. */
    @Input() filterId: string;

    // Properties
    _starttitle = 'Filtertool';
    icon = 'fas fa-filter';
    /** De gekoppelde filtertool-instantie voor dit scherm. */
    public filtertoolInstantie: FiltertoolInstantie | null = null;
    /** De actuele filterboom (root is altijd een EN-groep met clausules). */
    public boom: Filterboom = {soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null} as any;
    /** Mapping van regeltype naar label voor weergave. */
    public readonly regelTypeNamen = FiltertoolRegelTypeNamen;
    /** De lijst met opgeslagen filters voor de huidige gebruiker (geladen via GebruikerFilter.getAll()). */
    public opgeslagenFilters: GebruikerFilter[] = [];
    /** Het geselecteerde filterobject uit de keuzelijst. */
    public geselecteerdFilter: GebruikerFilter | null = null;
    /** Lookup van gebruikers per id, gebruikt om eigenaarnaam te tonen bij filters van anderen. */
    public gebruikersOpId: Record<number, Gebruiker> = {};
    /** De factoryservice voor filtertool-instanties, beschikbaar via field initializer. */
    private readonly filtertoolFactory = inject(FiltertoolFactoryService);
    /** Lokale kopie van de boom waar op bewerkt wordt. */
    private origineleBoom: Filterboom = { soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as any;
    /** De boom zoals die hoort bij het momenteel geselecteerde opgeslagen filter. */
    private geselecteerdFilterBoom: Filterboom | null = null;


    // Constructor
    constructor() {
        super('debug');
    }

    // Lifecycle hooks
    /** Haalt de gekoppelde filtertool-instantie op en initialiseert de lokale bewerkboom. */
    async ngOnInit(): Promise<void> {
        this.filtertoolInstantie = this.filterId ? (this.filtertoolFactory.opId(this.filterId) ?? null) : null;
        if (this.filtertoolInstantie) {
            this.origineleBoom = this.filtertoolInstantie.boom;
            this.boom = this.cloneBoom(this.origineleBoom);
        }
        await this.laadOpgeslagenFilters();

        // Herstel selectie van opgeslagen filter uit de gekoppelde filtertool-instantie (zonder de werkelijke boom te wijzigen)
        const hersteldId = this.filtertoolInstantie?.geselecteerdFilterId ?? null;
        if (hersteldId) {
            const gevonden = this.opgeslagenFilters.find(f => f.id === hersteldId) ?? null;
            if (gevonden) {
                this.geselecteerdFilter = gevonden;
                await gevonden.haalFilter();
                // Zet alleen de referentieboom van het geselecteerde filter; laat this.boom/origineleBoom ongemoeid (komen uit localStorage via instantie)
                let parsed: any;
                try {
                    parsed = gevonden.filter ? JSON.parse(gevonden.filter) : null;
                } catch {
                    parsed = null;
                }
                const struct = parsed ? filterboomVanObject(parsed) : ({ soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as any);
                this.geselecteerdFilterBoom = this.cloneBoom(struct);
            }
        }

        // Abonneer op wijzigingen in gebruiker_filter en herlaad de lijst zodra er iets wijzigt (via Appscherm.subscription)
        this.subscription(this.api.onDatawijziging('gebruiker_filter').subscribe(async () => {
            await this.laadOpgeslagenFilters();
            // Herstel selectie op basis van de instantie of huidige selectie-id
            const beoogdId = (this.filtertoolInstantie?.geselecteerdFilterId ?? null) ?? this.geselecteerdFilter?.id ?? null;
            this.geselecteerdFilter = beoogdId ? (this.opgeslagenFilters.find(f => f.id === beoogdId) ?? null) : null;
            if (this.geselecteerdFilter) {
                await this.geselecteerdFilter.haalFilter();
                let parsed: any;
                try {
                    parsed = this.geselecteerdFilter.filter ? JSON.parse(this.geselecteerdFilter.filter) : null;
                } catch {
                    parsed = null;
                }
                const struct = parsed ? filterboomVanObject(parsed) : ({ soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as any);
                this.geselecteerdFilterBoom = this.cloneBoom(struct);
                // schrijf (bevestig) selectie terug naar de filtertool-instantie
                if (this.filtertoolInstantie) {
                    this.filtertoolInstantie.geselecteerdFilterId = this.geselecteerdFilter.id ?? null;
                }
            } else {
                this.geselecteerdFilterBoom = null;
                // selectie is verdwenen (bijv. verwijderd): wis selectie in de filtertool-instantie
                if (this.filtertoolInstantie) {
                    this.filtertoolInstantie.geselecteerdFilterId = null;
                }
            }
        }));
    }


    // Public methods
    /**
     * Laadt alle opgeslagen filters van de gebruiker uit de backend via het model
     * en bewaart de resultaten lokaal. Haalt bewust niet het zware `filter`-veld op.
     */
    public async laadOpgeslagenFilters(): Promise<void> {
        try {
            // Haal alle filters op zonder het zware JSON-veld 'filter'
            const lijst = await GebruikerFilter.getAll(undefined, [
                [GebruikerFilter, { attributes: ['id','gebruiker_id','werkmaatschappij_id','naam','publiek'] }]
            ]);

            // Verzamel de unieke eigenaar-ids (behalve de huidige gebruiker) waarvoor we nog geen naam hebben
            const missendeGebruikerIds = new Set<number>();
            for (const f of lijst) {
                if (f.gebruiker_id && f.gebruiker_id !== this.api.uid) {
                    if (!this.gebruikersOpId[f.gebruiker_id]) {
                        missendeGebruikerIds.add(f.gebruiker_id);
                    }
                }
            }
            // Haal ontbrekende gebruikers op met een minimale attribute-set
            if (missendeGebruikerIds.size) {
                const gebruikers =await Gebruiker.getAll([],
                    [[Gebruiker,
                    {
                        attributes: ['id','naam'],
                        where: { id: [...missendeGebruikerIds] }
                    }]],
                    undefined,
                    GebruikerFilter
                    );
                for (const g of gebruikers) {
                    if (g && g.id) {
                        this.gebruikersOpId[g.id] = g;
                    }
                }
            }

            // Sorteer: eerst eigen filters, dan anderen; binnen elke groep op naam (case-insensitive)
            const compareNaam = (a: GebruikerFilter, b: GebruikerFilter) => {
                const an = (a.naam || '').toLocaleLowerCase();
                const bn = (b.naam || '').toLocaleLowerCase();
                if (an < bn) { return -1; }
                if (an > bn) { return 1; }
                return 0;
            };
            const eigen = lijst.filter(f => f.gebruiker_id === this.api.uid).sort(compareNaam);
            const anderen = lijst.filter(f => f.gebruiker_id !== this.api.uid).sort(compareNaam);
            this.opgeslagenFilters = [...eigen, ...anderen];
        } catch (e) {
            this.opgeslagenFilters = [];
        }
    }

    /**
     * Wordt aangeroepen wanneer de gebruiker een opgeslagen filter selecteert in de keuzelijst.
     * Laadt indien nodig de filter-JSON van het geselecteerde record en zet vervolgens de boom in het scherm.
     */
    public async opgeslagenFilterGeselecteerd(f: GebruikerFilter | null): Promise<void> {
        if (!f) { return; }
        // Leg de selectie vast op de gekoppelde filtertool-instantie
        if (this.filtertoolInstantie) {
            this.filtertoolInstantie.geselecteerdFilterId = f.id ?? null;
        }
        await f.haalFilter();
        // Parseer de JSON veilig; bij ontbrekend of ongeldige JSON valt de boom terug op een lege EN-root
        let parsed: any;
        try {
            parsed = f.filter ? JSON.parse(f.filter) : null;
        } catch (e) {
            parsed = null;
        }
        const nieuweStruct = parsed ? filterboomVanObject(parsed) : ({ soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as any);
        // Zet de referentieboom van het geselecteerde opgeslagen filter en maak de bewerkbare boom gelijk hieraan
        this.geselecteerdFilterBoom = this.cloneBoom(nieuweStruct);
        this.boom = this.cloneBoom(nieuweStruct);
        // Let op: origineleBoom (de laatst toegepaste boom) blijft onveranderd voor scherm-dirty check
    }

    /** Verwijdert een clausule (regel of groep) uit de lokale boom. */
    onRegelVerwijder(id: string): void {
        this.verwijderClausule(this.boom, id);
        // Opruimen: verwijder lege groepen (behalve root) die hierdoor kunnen ontstaan
        this.verwijderLegeGroepen();
    }

    /**
     * Verplaatst een regel één niveau omhoog: uit de huidige groep, naar de bovenliggende groep.
     * Wordt genegeerd als de regel al direct onder de rootgroep hangt.
     */
    onVerplaatsRegelNaarParent(id: string): void {
        const loc = this.vindParentEnIndex(id);
        if (!loc || !loc.parent) { return; }
        const greatParent = loc.parent.parentID ? this.vindGroep(loc.parent.parentID) : null;
        if (!greatParent) { return; }
        const regel = loc.parent.kinderen.splice(loc.indexInParent, 1)[0];
        const idxParentInGreatParent = greatParent.kinderen.findIndex(k => k.id === loc.parent.id);
        const insertIndex = (idxParentInGreatParent >= 0) ? idxParentInGreatParent + 1 : greatParent.kinderen.length;
        greatParent.kinderen.splice(insertIndex, 0, regel);
        // Zet nieuwe parentID op de verplaatste regel
        if (regel && regel.soort === 'regel') {
            (regel as any).parentID = greatParent.id;
        }
        // Opruimen: mogelijk is de bron‑groep leeg geraakt; verwijder lege groepen (niet‑root)
        this.verwijderLegeGroepen();
    }

    /**
     * Wikkelt een regel in een nieuwe subgroep met opgegeven operator en vervangt de regel op dezelfde positie.
     */
    onWikkelRegel(ev: { clausuleId: string; operator: 'en' | 'of' }): void {
        const id = ev?.clausuleId;
        const operator = ev?.operator === 'of' ? 'of' : 'en';
        const loc = this.vindParentEnIndex(id);
        if (!loc || !loc.parent) { return; }
        const regel = loc.parent.kinderen.splice(loc.indexInParent, 1)[0];
        const nieuweGroep = { soort: 'groep', id: crypto.randomUUID(), operator, kinderen: [regel], parentID: loc.parent.id } as any;
        // Zet parentID van de ingepakte regel op de nieuwe groep
        if (regel && regel.soort === 'regel') {
            (regel as any).parentID = nieuweGroep.id;
        }
        loc.parent.kinderen.splice(loc.indexInParent, 0, nieuweGroep);
    }

    /** Past de lokale boom toe op de instantie en triggert toepassen. */
    pasToe(): void {
        if (!this.filtertoolInstantie) { return; }
        this.filtertoolInstantie.boom = this.boom;
        this.filtertoolInstantie.pasToe();
        this.origineleBoom = this.filtertoolInstantie.boom; //dit is altijd een kopie
    }

    /** Sluit het venster zonder wijzigingen toe te passen. */
    annuleer(): void {
        this.closeWindow();
    }

    /** Past toe en sluit het venster. */
    opslaan(): void {
        this.pasToe();
        this.closeWindow();
    }

    /**
     * Voegt binnen de groep met gegeven id een nieuwe regel of subgroep toe op basis van het gekozen type.
     */
    public voegNieuweRegelToeInGroep(ev: { groepId: string; type: (typeof FiltertoolRegelTypes)[number] | '--EN' | '--OF' }): void {
        const { groepId, type } = ev || ({} as any);
        if (!groepId || !type) { return; }
        const groep = this.vindGroep(groepId);
        if (!groep) { return; }
        // Speciaal: '--EN' en '--OF' representeren het toevoegen van een subgroep
        switch (type) {
            case '--EN':
                groep.kinderen.push({ soort: 'groep', id: crypto.randomUUID(), operator: 'en', kinderen: [], parentID: groep.id } as any);
                break;
            case '--OF':
                groep.kinderen.push({ soort: 'groep', id: crypto.randomUUID(), operator: 'of', kinderen: [], parentID: groep.id } as any);
                break;
            default:
                const nieuweClausule = { soort: 'regel', id: crypto.randomUUID(), regel: this.maakNieuweRegel(type), parentID: groep.id } as any;
                groep.kinderen.push(nieuweClausule);
        }
    }

    // Getters voor UI-state
    /** Geeft aan of er wijzigingen zijn t.o.v. de originele boom. */
    get isDirty(): boolean {
        return JSON.stringify(this.boom) !== JSON.stringify(this.origineleBoom);
    }
    /** Geeft aan of de huidige bewerkboom geldig is volgens de regels. */
    get isValid(): boolean { return isFilterboomGeldig(this.boom); }

    /** Geeft aan of het geselecteerde filter van de huidige gebruiker is. */
    get isEigenGeselecteerdFilter(): boolean {
        return !!this.geselecteerdFilter && this.geselecteerdFilter.gebruiker_id === this.api.uid;
    }

    /** Geeft aan of de huidige bewerkboom afwijkt van de boom van het geselecteerde opgeslagen filter. */
    get isOpgeslagenFilterDirty(): boolean {
        if (!this.geselecteerdFilter) { return false; }
        const boomDirty = this.geselecteerdFilterBoom ? (JSON.stringify(this.boom) !== JSON.stringify(this.geselecteerdFilterBoom)) : false;
        const publiekOrig = !!this.geselecteerdFilter.orig('publiek');
        const publiekDirty = (!!this.geselecteerdFilter.publiek) !== publiekOrig;
        return boomDirty || publiekDirty;
    }

    /** Bepaalt of de knop "Huidige filter opslaan" actief mag zijn. */
    get kanOpslaanFilter(): boolean {
        return this.isOpgeslagenFilterDirty && !!this.geselecteerdFilter && this.isEigenGeselecteerdFilter;
    }

    /** Geeft aan of de knop "Filter verwijderen" actief mag zijn. Alleen bij een eigen geselecteerd filter. */
    get kanVerwijderenFilter(): boolean {
        return !!this.geselecteerdFilter && this.isEigenGeselecteerdFilter;
    }

    /**
     * Slaat de huidige boom op in het geselecteerde gebruikersfilter zonder toe te passen op het scherm.
     * Na succesvol opslaan wordt de referentieboom voor het geselecteerde filter gelijkgetrokken,
     * zodat de filter-dirty status uitgaat terwijl de scherm-dirty status ongemoeid blijft.
     */
    async slaHuidigFilterOp(): Promise<void> {
        if (!this.kanOpslaanFilter) { return; }
        try {
            this.geselecteerdFilter.filter = JSON.stringify(filterboomNaarObject(this.boom));
            await this.geselecteerdFilter.save();
            // Filter is nu gelijk aan de bewerkboom: zet de referentie voor filter-dirty op schoon
            this.geselecteerdFilterBoom = this.cloneBoom(this.boom);
            // Geen handmatige refresh: de cache wordt geleegd en de subscription haalt de lijst opnieuw op
            this.api.toast('Filter opgeslagen', 'success');
        } catch (e) {
            // api toont al een fouttoast indien nodig
        }
    }

    /**
     * Vraagt een naam en slaat de huidige boom op als nieuw gebruikersfilter.
     * Past de boom niet toe op de instantie; na opslaan wordt het nieuwe filter geselecteerd
     * en wordt de referentieboom voor het opgeslagen filter gelijkgezet aan de bewerkboom.
     */
    async slaHuidigFilterOpAls(): Promise<void> {
        try {
            const voorgesteldeNaam = this.geselecteerdFilter?.naam || 'Nieuw filter';
            const naam = window.prompt('Naam voor het opgeslagen filter:', voorgesteldeNaam);
            if (!naam || !naam.trim()) { return; }
            const nieuw = GebruikerFilter.createNew(null, {
                gebruiker_id: this.api.uid,
                werkmaatschappij_id: this.api.werkmaatschappij?.id ?? null,
                naam: naam.trim(),
                publiek: false,
                filter: JSON.stringify(filterboomNaarObject(this.boom))
            });
            await nieuw.save();
            // Leg de nieuwe selectie vast op de gekoppelde filtertool-instantie
            if (this.filtertoolInstantie) {
                this.filtertoolInstantie.geselecteerdFilterId = nieuw.id ?? null;
            }
            // Selecteer direct het nieuwe filter en zet de referentieboom gelijk aan de huidige bewerkboom
            this.geselecteerdFilter = this.opgeslagenFilters.find(f => f.id === nieuw.id) ?? nieuw;
            this.geselecteerdFilterBoom = this.cloneBoom(this.boom);
            this.api.toast('Filter opgeslagen als nieuw', 'success');
        } catch (e) {
            // api toont al een fouttoast indien nodig
        }
    }

    /**
     * Leegt de huidige filterbewerking en wist tevens de selectie van opgeslagen filters.
     * Dit zorgt ervoor dat er bewerkt wordt in een losse, niet‑opgeslagen filterboom.
     * De instantie‑selectie (`geselecteerdFilterId`) wordt ook gewist, zodat clients dit kunnen herkennen.
     */
    public maakNieuwFilter(): void {
        // Leeg de bewerkboom
        this.boom = { soort: 'groep', id: 'root', operator: 'en', kinderen: [], parentID: null } as any;
        // Maak de selectie van opgeslagen filters expliciet leeg
        this.geselecteerdFilter = null;
        this.geselecteerdFilterBoom = null;
        if (this.filtertoolInstantie) {
            (this.filtertoolInstantie as any).geselecteerdFilterId = null;
        }
        // origineleBoom laten staan, zodat isDirty true wordt en de gebruiker kan kiezen wat te doen
    }

    /** Verwijdert het geselecteerde gebruikersfilter na bevestiging via de api.confirm dialoog. */
    async verwijderGeselecteerdFilter(): Promise<void> {
        const f = this.geselecteerdFilter;
        if (!f?.id || !this.isEigenGeselecteerdFilter) { return; }
        const akkoord = await this.api.confirm('Filter verwijderen', `Weet je zeker dat je het opgeslagen filter "${f.naam}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`);
        if (!akkoord) { return; }
        try {
            await this.api.delete(GebruikerFilter.table, f.id);
            this.api.toast('Filter verwijderd', 'success');
            // Maak selectie leeg; de boom laten we gewoon staan
            this.geselecteerdFilter = null;
            this.geselecteerdFilterBoom = null;
            if (this.filtertoolInstantie) {
                this.filtertoolInstantie.geselecteerdFilterId = null;
            }
        } catch (e) {
            // api toont al een fouttoast indien nodig
        }
    }


    /**
     * Zoek parent en index van de clausule binnen de parent.
     */
    private vindParentEnIndex(id: string): { parent: any; indexInParent: number } | null {
        // Vind eerst de clausule zelf en gebruik vervolgens de parentID om de parent snel te vinden.
        const node: any = this.vindClausule(id);
        if (!node || node.parentID == null) { return null; }
        const parent: any = this.vindGroep(node.parentID);
        if (!parent || !Array.isArray(parent.kinderen)) { return null; }
        const indexInParent = parent.kinderen.findIndex((k: any) => k?.id === id);
        return indexInParent >= 0 ? { parent, indexInParent } : null;
    }

    /** Diepe clone van een object. */
    /**
     * Maakt een diepe kopie van de boom en behoudt de juiste class‑instanties per regel
     * door een serialize/parse rondje via de filtertool‑structuur helpers.
     */
    private cloneBoom(v: Filterboom): Filterboom {
      return filterboomVanObject(filterboomNaarObject(v));
    }

    /** Zoek een groep in de lokale boom op id. */
    private vindGroep(groepId: string, start?: any): any {
        const node = start ?? this.boom;
        if (!node) { return null; }
        if (node.soort === 'groep' && node.id === groepId) { return node; }
        if (node.soort === 'groep' && Array.isArray(node.kinderen)) {
            for (const k of node.kinderen) {
                const gevonden = this.vindGroep(groepId, k);
                if (gevonden) { return gevonden; }
            }
        }
        return null;
    }

    /** Zoek willekeurige clausule (regel of groep) op id. */
    private vindClausule(clausuleId: string, start?: any): any {
        const node = start ?? this.boom;
        if (!node) { return null; }
        if (node.id === clausuleId) { return node; }
        if (node.soort === 'groep' && Array.isArray(node.kinderen)) {
            for (const k of node.kinderen) {
                const gevonden = this.vindClausule(clausuleId, k);
                if (gevonden) { return gevonden; }
            }
        }
        return null;
    }

    /** Verwijder clausule met id uit de boom. */
    private verwijderClausule(groep: any, id: string): boolean {
        this.log.debug('Verwijderen van clausule met id', id);
        if (!groep || groep.soort !== 'groep') { return false; }
        const oudLen = groep.kinderen.length;
        groep.kinderen = groep.kinderen.filter((k: any) => k.id !== id);
        if (groep.kinderen.length !== oudLen) { return true; }
        for (const k of groep.kinderen) {
            if (k.soort === 'groep') {
                const verwijderd = this.verwijderClausule(k, id);
                if (verwijderd) { return true; }
            }
        }
        return false;
    }

    /**
     * Verwijdert leeggeraakte subgroepen in de hele boom, met uitzondering van de root-groep.
     * Deze methode wordt aangeroepen na verplaatsen of verwijderen van clausules.
     */
    private verwijderLegeGroepen(): void {
        this.verwijderLegeGroepenRec(this.boom);
    }

    /**
     * Recursieve helper: loopt door alle groepen heen, ruimt eerst dieper op en
     * verwijdert daarna op dit niveau lege subgroepen (k.kinderen.length === 0), behalve de root.
     */
    private verwijderLegeGroepenRec(node: any): void {
        if (!node || node.soort !== 'groep' || !Array.isArray(node.kinderen)) { return; }
        // Eerst dieper opruimen
        for (const kind of node.kinderen) {
            if (kind?.soort === 'groep') {
                this.verwijderLegeGroepenRec(kind);
            }
        }
        // Verwijder lege subgroepen op dit niveau (root nooit verwijderen)
        node.kinderen = node.kinderen.filter((k: any) => {
            if (k?.soort === 'groep') {
                const leeg = !k.kinderen || k.kinderen.length === 0;
                if (leeg && k.id !== 'root') {
                    return false;
                }
            }
            return true;
        });
        // Het actuele node‑niveau zelf blijft bestaan; eventuele leegte wordt door de ouder gefilterd.
    }


    /** Maak een nieuwe regel met standaardvelden afhankelijk van type. */
    private maakNieuweRegel(type: typeof FiltertoolRegelTypes[number]): Filtertoolregel {
          // Delegeer naar de class-fabriek zodat we altijd de juiste subclass krijgen
          return maakFiltertoolregel(type) as any;
      }

}

// Zelfregistratie van dit scherm bij de windowmanager
Filtertoolscherm.register('Filtertoolscherm');
